import {Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {CustomerDetails} from '../../../models/warranties/warranty.model';
import {HttpService} from '../../../services/http.service';
import {Dealer} from '../../../models/dealers/dealer.model';
import {I18nProvider} from '../../../services/i18n.service';
import {DealerService} from '../../dealer/dealer.service';
import {AuthService} from '../../../auth/auth.service';
import {Market} from '../../../models/dealers/market.enum';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

@Component({
  selector: 'app-customer-details',
  templateUrl: './customer-details.component.html',
  styleUrls: ['./customer-details.component.scss']
})
export class CustomerDetailsComponent implements OnInit, OnChanges {

  constructor(private http: HttpService, private i18nProvider: I18nProvider, private dealerService: DealerService,
              private authService: AuthService, private formBuilder: UntypedFormBuilder) {

    this.customerDetailsForm = this.formBuilder.group({
      firstName: [''],
      lastName: [''],
      middleName: [''],
      initials: [''],
      dateOfBirth: [''],
      gender: [''],
      telephone: [''],
      email: [''],
      streetNumber: [''],
      street: [''],
      postCode: [''],
      city: [''],
      company: [''],
      id: [],
    });
  }

  @Input() customer: CustomerDetails;
  @Input() showSearch: boolean;
  @Input() editable = true;
  @Input() dealer: string;
  @Input() dealerOptions: Dealer[] = [];
  @ViewChild('searchFirst') searchFirst;

  firstNameError = '';
  lastNameError = '';
  phoneError = '';
  searchFirstName = '';
  searchLastName = '';
  searchOptions: CustomerDetails[] = null;
  searchError = '';
  selectedBuno: string;
  market: Market;
  showField = false;
  isAdminUser = false;
  customerDetailsForm: UntypedFormGroup;
  mandatoryIndicator: string;

  ngOnInit(): void {
    this.showField = Market.NL === this.authService.market();
    if (this.customer && this.dealerOptions) {
      this.selectDealer(this.dealerOptions);
    }
    this.isAdminUser = this.authService.isAdmin();
    this.updateValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.customer) {
      this.updateCustomerInfo();
    }
  }

  private updateCustomerInfo() {
    this.customerDetailsForm.setValue({
      firstName: this.customer.firstName ? this.customer.firstName : '',
      lastName: this.customer.lastName ? this.customer.lastName : '',
      middleName: this.customer.middleName ? this.customer.middleName : '',
      initials: this.customer.initials ? this.customer.initials : '',
      dateOfBirth: this.customer.dateOfBirth ? this.customer.dateOfBirth : new Date(),
      gender: this.customer.gender ? this.customer.gender : '',
      telephone: this.customer.telephone ? this.customer.telephone : '',
      email: this.customer.email ? this.customer.email : '',
      streetNumber: this.customer.streetNumber ? this.customer.streetNumber : '',
      street: this.customer.street ? this.customer.street : '',
      postCode: this.customer.postCode ? this.customer.postCode : '',
      city: this.customer.city ? this.customer.city : '',
      company: this.customer.company ? this.customer.company : '',
      id: this.customer.id ? this.customer.id : 0,
    });
  }

  private selectDealer(dealers: Dealer[]) {
    const selectedDealers = dealers.filter((dealer) => this.dealer === dealer.dealerName);
    if (selectedDealers && selectedDealers.length > 0) {
      this.selectedBuno = selectedDealers[0].buno;
    } else {
      if (this.dealer) {
        this.dealerService.dealersByName(this.dealer).subscribe((sellingDealers: Array<Dealer>) => {
          if (sellingDealers && sellingDealers.length > 0) {
            const selectedDealer = sellingDealers[0];
            if (this.dealerOptions.filter((duplicateCandidate: Dealer) => duplicateCandidate.dealerName === this.dealer).length === 0) {
              this.dealerOptions.push(selectedDealer);
            }
            this.selectedBuno = selectedDealer.buno;
          }
        });
      }
    }
  }

  searchForCustomer() {
    this.searchOptions = null;
    this.searchError = '';
    this.http.searchForCustomer(this.searchFirstName, this.searchLastName, this.selectedBuno).subscribe((res) => {
      this.searchOptions = Object.assign([], res);
      if (this.searchOptions.length === 0) {
        this.searchError = this.i18nProvider.getDescription('no-records-found');
        this.searchFirst.nativeElement.focus();
        this.searchFirst.nativeElement.select();
      }
    });
  }

  selectSearchOption(c: CustomerDetails) {
    Object.assign(this.customer, c);
  }

  updateValidators() {
    if (this.editable) {
      this.customerDetailsForm.enable();
      this.customerDetailsForm.get('firstName').setValidators([Validators.required, Validators.minLength(2)]);
      this.customerDetailsForm.get('lastName').setValidators([Validators.required, Validators.minLength(2)]);
      const isMarketBE = Market.BE === this.authService.market();
      if (!isMarketBE) {
        this.mandatoryIndicator = '*';
        this.customerDetailsForm.get('initials').setValidators(Validators.required);
        // this.customerDetailsForm.get('telephone').setValidators([Validators.required, Validators.minLength(10),
        //   Validators.maxLength(10)]);
        this.customerDetailsForm.get('streetNumber').setValidators([Validators.required, Validators.minLength(1)]);
        this.customerDetailsForm.get('street').setValidators(Validators.required);
        this.customerDetailsForm.get('postCode').setValidators(Validators.required);
        this.customerDetailsForm.get('city').setValidators(Validators.required);
      } else {
        this.mandatoryIndicator = '';
        this.customerDetailsForm.get('streetNumber').setValidators([Validators.minLength(1)]);
      }
      // this.customerDetailsForm.get('dateOfBirth').setValidators(Validators.required);
      // this.customerDetailsForm.get('gender').setValidators(Validators.required);
      this.customerDetailsForm.updateValueAndValidity();
    } else {
      this.customerDetailsForm.get('firstName').clearValidators();
      this.customerDetailsForm.get('firstName').updateValueAndValidity();
      this.customerDetailsForm.get('firstName').disable();

      this.customerDetailsForm.get('lastName').clearValidators();
      this.customerDetailsForm.get('lastName').updateValueAndValidity();
      this.customerDetailsForm.get('lastName').disable();

      this.customerDetailsForm.get('initials').clearValidators();
      this.customerDetailsForm.get('initials').updateValueAndValidity();
      this.customerDetailsForm.get('initials').disable();

      this.customerDetailsForm.get('telephone').clearValidators();
      this.customerDetailsForm.get('telephone').updateValueAndValidity();
      this.customerDetailsForm.get('telephone').disable();

      this.customerDetailsForm.get('streetNumber').clearValidators();
      this.customerDetailsForm.get('streetNumber').updateValueAndValidity();
      this.customerDetailsForm.get('streetNumber').disable();

      this.customerDetailsForm.get('street').clearValidators();
      this.customerDetailsForm.get('street').updateValueAndValidity();
      this.customerDetailsForm.get('street').disable();

      this.customerDetailsForm.get('postCode').clearValidators();
      this.customerDetailsForm.get('postCode').updateValueAndValidity();
      this.customerDetailsForm.get('postCode').disable();

      this.customerDetailsForm.get('city').clearValidators();
      this.customerDetailsForm.get('city').updateValueAndValidity();
      this.customerDetailsForm.get('city').disable();

      this.customerDetailsForm.get('dateOfBirth').clearValidators();
      this.customerDetailsForm.get('dateOfBirth').updateValueAndValidity();
      this.customerDetailsForm.get('dateOfBirth').disable();

      this.customerDetailsForm.get('gender').clearValidators();
      this.customerDetailsForm.get('gender').updateValueAndValidity();
      this.customerDetailsForm.get('gender').disable();

      this.customerDetailsForm.get('email').disable();
      this.customerDetailsForm.get('middleName').disable();
      this.customerDetailsForm.get('company').disable();
    }
  }

  isFieldValid(controlName: string): boolean {
    const control = this.customerDetailsForm.get(controlName);
    return control.invalid && (control.dirty || control.touched);
  }

  public isValid() {
    this.customerDetailsForm.markAllAsTouched();
    return this.customerDetailsForm.valid;
  }

  public getCustomerDetails(): CustomerDetails {
    return this.customerDetailsForm.value;
  }
}
